// V2 Article detail — view sanitised HTML, flip draft/published, launch Promote flow.
// Reads /api/pages/:slug (kind=article filtered by BE via container lookup).
import { Fragment, useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  Center,
  Group,
  Loader,
  Menu,
  Paper,
  Select,
  SegmentedControl,
  Stack,
  Table,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconChevronDown, IconChevronRight, IconCircleCheck, IconDots, IconExternalLink, IconRocket, IconTrash } from '@tabler/icons-react';
import useApi from '../hooks/useApi';
import {
  deleteArticle,
  fetchArticle,
  fetchArticleAnalytics,
  fetchArticleTemplates,
  PUBLIC_PAGE_BASE_URL,
  updateArticle,
  updateArticleTemplate,
} from '../services/api';

function statusPillProps(status) {
  if (status === 'promoting') return { color: 'sage', variant: 'filled', label: 'promoting' };
  if (status === 'published') return { color: 'sage', variant: 'light', label: 'published' };
  return { color: 'gray', variant: 'light', label: 'draft' };
}

function shortDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

function formatAgo(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = Date.now() - then;
  if (diffMs < 60_000) return 'just now';
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function StatTile({ label, value, hint, color }) {
  return (
    <Paper p="lg" radius="md" withBorder style={{ background: '#fff', flex: 1, minWidth: 140 }}>
      <Stack gap={4}>
        <Text fw={800} style={{ color: color || 'var(--aviary-ink)', fontSize: 32, lineHeight: 1 }}>
          {value}
        </Text>
        <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.3, textTransform: 'uppercase' }}>
          {label}
        </Text>
        {hint && (
          <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>{hint}</Text>
        )}
      </Stack>
    </Paper>
  );
}

function outcomeBadge(outcome) {
  if (outcome === 'reactivated') return <Badge color="yellow" variant="filled" size="sm">reactivated</Badge>;
  if (outcome === 'booked') return <Badge color="sage" variant="filled" size="sm">booked</Badge>;
  return <Badge color="gray" variant="light" size="sm">logged</Badge>;
}

function formatLocation({ city, region, country } = {}) {
  const parts = [];
  if (city) parts.push(String(city));
  if (region && !parts.includes(region)) parts.push(String(region));
  const head = parts.join(' ');
  if (head && country) return `${head}, ${country}`;
  if (head) return head;
  if (country) return String(country);
  return '—';
}

function formatDevice({ browser, os, deviceModel, device } = {}) {
  if (deviceModel && browser) return `${deviceModel} · ${browser}`;
  if (os && browser) return `${os} · ${browser}`;
  if (browser) return browser;
  if (os) return os;
  if (device && device !== 'unknown') return device;
  return '—';
}

function formatDwell(seconds) {
  const n = Number(seconds);
  if (!Number.isFinite(n) || n <= 0) return '—';
  if (n < 60) return `${n}s`;
  const m = Math.floor(n / 60);
  const s = n % 60;
  if (m < 60) return s > 0 ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return mm > 0 ? `${h}h ${mm}m` : `${h}h`;
}

function formatReadPct(pct) {
  const n = Number(pct);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return `${Math.min(100, Math.max(0, Math.round(n)))}%`;
}

function formatVisitTs(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
}

export default function AviaryV2ArticleDetailPage() {
  const api = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useParams();
  const back = location.state?.back || { path: '/v2/articles', label: 'Articles' };

  const [article, setArticle] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [savingRole, setSavingRole] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [expandedByLeadId, setExpandedByLeadId] = useState({});
  const [expandedReaderKey, setExpandedReaderKey] = useState({});
  const [showAllByLeadId, setShowAllByLeadId] = useState({});

  useEffect(() => {
    let cancelled = false;
    fetchArticle(api, slug)
      .then((data) => { if (!cancelled) setArticle(data); })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.error || err.message); });
    fetchArticleAnalytics(api, slug)
      .then((data) => { if (!cancelled) setAnalytics(data); })
      .catch(() => { /* silent — analytics failure shouldn't break the page */ });
    fetchArticleTemplates(api)
      .then((data) => { if (!cancelled) setTemplates(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setTemplates([]); });
    return () => { cancelled = true; };
  }, [api, slug]);

  useEffect(() => {
    setExpandedByLeadId({});
    setExpandedReaderKey({});
    setShowAllByLeadId({});
  }, [slug]);

  async function toggleStatus() {
    if (!article) return;
    const nextStatus = article.articleStatus === 'draft' ? 'published' : 'draft';
    setSaving(true);
    try {
      const updated = await updateArticle(api, slug, { articleStatus: nextStatus });
      setArticle(updated);
      notifications.show({
        title: nextStatus === 'published' ? 'Article published' : 'Reverted to draft',
        message: nextStatus === 'published'
          ? 'The public URL is live.'
          : 'The public URL now returns 404.',
        color: 'sage',
      });
    } catch (err) {
      notifications.show({
        title: 'Could not update',
        message: err.response?.data?.error || err.message,
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!article) return;
    if (!window.confirm(`Delete "${article.title}"? This can't be undone.`)) return;
    setDeleting(true);
    try {
      await deleteArticle(api, slug);
      navigate('/v2/articles');
    } catch (err) {
      notifications.show({
        title: 'Could not delete',
        message: err.response?.data?.error || err.message,
        color: 'red',
      });
      setDeleting(false);
    }
  }

  async function changeTemplate(nextSlug) {
    if (!article || !nextSlug || savingTemplate) return;
    const previous = String(article.templateSlug || 'professional');
    if (nextSlug === previous) return;
    setSavingTemplate(true);
    setArticle((prev) => (prev ? { ...prev, templateSlug: nextSlug } : prev));
    try {
      const updated = await updateArticleTemplate(api, slug, nextSlug);
      setArticle(updated);
      notifications.show({
        title: 'Look updated',
        message: 'Public article now uses the selected look.',
        color: 'sage',
      });
    } catch (err) {
      setArticle((prev) => (prev ? { ...prev, templateSlug: previous } : prev));
      notifications.show({
        title: 'Could not update look',
        message: err.response?.data?.error || err.message,
        color: 'red',
      });
    } finally {
      setSavingTemplate(false);
    }
  }

  async function changeArticleRole(nextRole) {
    if (!article || !nextRole || savingRole) return;
    const previousRole = String(article.articleRole || 'promo');
    if (nextRole === previousRole) return;
    setSavingRole(true);
    setArticle((prev) => (prev ? { ...prev, articleRole: nextRole } : prev));
    try {
      const updated = await updateArticle(api, slug, { articleRole: nextRole });
      setArticle(updated);
      notifications.show({
        title: nextRole === 'context' ? 'Marked as context' : 'Marked as promo',
        message: nextRole === 'context'
          ? 'Agent context payload now includes this article.'
          : 'This article is now treated as a promotion asset.',
        color: 'sage',
      });
    } catch (err) {
      setArticle((prev) => (prev ? { ...prev, articleRole: previousRole } : prev));
      notifications.show({
        title: 'Could not update article role',
        message: err.response?.data?.error || err.message,
        color: 'red',
      });
    } finally {
      setSavingRole(false);
    }
  }

  if (error) {
    return (
      <Center py={80} px="lg">
        <Stack gap="sm" align="center">
          <Text fw={600} c="red">Couldn't load article</Text>
          <Text size="sm" c="dimmed">{error}</Text>
        </Stack>
      </Center>
    );
  }
  if (!article) {
    return (
      <Center py={80}>
        <Loader color="sage" />
      </Center>
    );
  }

  const status = article.articleStatus || 'draft';
  const pill = statusPillProps(status);
  const publicHref = `${PUBLIC_PAGE_BASE_URL}/p/${article.principalId}/${slug}`;
  const isExternalArticle = String(article.kind || '').toLowerCase() === 'external';
  const renderMode = String(article.renderMode || 'template').toLowerCase();
  const isRawArticle = renderMode === 'raw';
  const canPromote = status !== 'draft'; // must publish before promoting
  const templateOptions = (templates.length ? templates : [
    { slug: 'professional', displayName: 'Professional' },
    { slug: 'modern', displayName: 'Modern' },
    { slug: 'creative', displayName: 'Creative' },
  ]).map((item) => ({ value: item.slug, label: item.displayName }));
  const templateSlug = String(article.templateSlug || 'professional');
  const articleRole = String(article.articleRole || 'promo').toLowerCase() === 'context'
    ? 'context'
    : 'promo';

  return (
    <Box style={{ padding: '32px 24px 120px', maxWidth: 900, margin: '0 auto' }}>
      <Stack gap="lg">
        <UnstyledButton
          onClick={() => navigate(back.path)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            color: 'var(--aviary-muted)',
            fontSize: 13,
            width: 'fit-content',
          }}
        >
          <IconArrowLeft size={14} /> Back to {back.label}
        </UnstyledButton>

        <Stack gap={6}>
          <Group gap="xs" align="center">
            <Text fw={700} style={{ color: 'var(--aviary-ink)', fontSize: 28 }}>
              {article.title || 'Untitled article'}
            </Text>
            <Badge color={pill.color} variant={pill.variant} size="md" radius="sm">
              {pill.label}
            </Badge>
          </Group>
          <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>
            {article.originNote || 'Uploaded HTML'}
            {article.updatedAt ? ` · updated ${shortDate(article.updatedAt)}` : ''}
            {typeof article.promotionCount === 'number' && article.promotionCount > 0
              ? ` · promoted ${article.promotionCount}×`
              : ''}
          </Text>
        </Stack>

        <Group gap="sm">
          <Button
            color="sage"
            leftSection={<IconRocket size={16} />}
            disabled={!canPromote}
            onClick={() => navigate(`/v2/articles/${slug}/promote`, {
              state: { back: { path: `/v2/articles/${slug}`, label: article.title } },
            })}
          >
            Promote
          </Button>
          <Button variant="default" onClick={toggleStatus} loading={saving}>
            {status === 'draft' ? 'Publish' : 'Move to draft'}
          </Button>
          {!isRawArticle && !isExternalArticle && (
            <Group gap={6} align="center" wrap="nowrap">
              <Text size="sm" c="dimmed">Look:</Text>
              <Select
                data={templateOptions}
                value={templateSlug}
                onChange={(value) => changeTemplate(value || '')}
                w={170}
                disabled={savingTemplate}
              />
            </Group>
          )}
          <Group gap={6} align="center" wrap="nowrap">
            <Text size="sm" c="dimmed">Role:</Text>
            <SegmentedControl
              size="xs"
              data={[
                { label: 'Promo', value: 'promo' },
                { label: 'Context', value: 'context' },
              ]}
              value={articleRole}
              onChange={changeArticleRole}
              disabled={savingRole}
            />
          </Group>
          {status !== 'draft' && !isExternalArticle && (
            <Button
              variant="subtle"
              leftSection={<IconExternalLink size={14} />}
              component="a"
              href={publicHref}
              target="_blank"
              rel="noreferrer"
            >
              View public
            </Button>
          )}
          <Menu shadow="md" position="bottom-end" withArrow>
            <Menu.Target>
              <Button variant="subtle" size="sm">
                <IconDots size={16} />
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                color="red"
                leftSection={<IconTrash size={14} />}
                onClick={handleDelete}
                disabled={deleting}
              >
                Delete
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>

        <Paper p="sm" radius="md" withBorder style={{ background: 'var(--aviary-surface)' }}>
          <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>
            Promo: Promotable to prospects. Context: Agent reads this - never promoted.
            Context articles are sent in `GET /api/v2/agent/context` and included in
            `GET /api/v2/agent/next-lead`.
          </Text>
        </Paper>

        {!canPromote && (
          <Paper p="sm" radius="md" withBorder style={{ background: 'var(--aviary-surface)' }}>
            <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>
              Publish this article before you can promote it. Draft articles are hidden from the public URL.
            </Text>
          </Paper>
        )}

        {/* Analytics — inline. Was a separate page; merged 2026-08-17. */}
        {analytics && (
          <Stack gap="md">
            {analytics.linkedUrl && (
              <Paper p="sm" radius="md" withBorder style={{
                background: 'var(--mantine-color-sage-0)',
                borderColor: 'var(--mantine-color-sage-3)',
              }}>
                <Text size="sm" style={{ color: 'var(--mantine-color-sage-8)' }}>
                  {isExternalArticle
                    ? (
                      <>
                        This article is hosted at{' '}
                        <a href={analytics.linkedUrl} target="_blank" rel="noreferrer">{analytics.linkedUrl}</a>.
                        {' '}Engagement tracking works once the install script is on that page.
                      </>
                    )
                    : `Aviary tracks opens on this article - the page itself lives on ${analytics.linkedDomain || 'an external site'} so time-on-page and reactions aren't captured.`}
                </Text>
              </Paper>
            )}

            {analytics.pendingReactivations > 0 && (
              <Paper p="md" radius="md" withBorder style={{
                background: 'var(--mantine-color-yellow-0, #fff9db)',
                borderColor: 'var(--mantine-color-yellow-4)',
              }}>
                <Group justify="space-between" align="center" wrap="nowrap">
                  <Group gap="xs" align="center" wrap="nowrap">
                    <IconCircleCheck size={18} style={{ color: 'var(--mantine-color-yellow-8)' }} />
                    <Text size="sm" style={{ color: 'var(--aviary-ink)' }}>
                      <strong>{analytics.pendingReactivations}</strong> {analytics.pendingReactivations === 1 ? 'reactivation' : 'reactivations'} waiting in your queue.
                    </Text>
                  </Group>
                  <Button variant="filled" color="yellow" size="xs" onClick={() => navigate('/v2')}>
                    Review {analytics.pendingReactivations} →
                  </Button>
                </Group>
              </Paper>
            )}

            <Group gap="md" wrap="wrap">
              <StatTile
                label="Links sent"
                value={analytics.stats.linksSent}
                hint={analytics.stats.linksSent === 0 ? 'Nothing promoted yet' : 'unique recipients'}
              />
              <StatTile
                label="Opened"
                value={analytics.stats.opened}
                hint={analytics.stats.linksSent > 0 ? `${analytics.stats.openedPct}% open rate` : null}
              />
              <StatTile
                label="Reactivated"
                value={analytics.stats.reactivated}
                color="var(--mantine-color-yellow-8)"
                hint={analytics.stats.reactivated > 0 ? 'cold prospects re-entered pipeline' : null}
              />
              <StatTile
                label="Booked"
                value={analytics.stats.booked}
                color="var(--mantine-color-sage-7)"
                hint={analytics.stats.booked > 0 ? 'attributed to this article' : null}
              />
              <StatTile
                label="Forwards"
                value={Number(analytics.forwardedReaderCount || 0)}
              />
            </Group>

            <Paper radius="md" withBorder p={0} style={{ background: '#fff', overflow: 'hidden' }}>
              <Box p="md" style={{ borderBottom: '1px solid var(--aviary-disabled)' }}>
                <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.3 }}>
                  WHO READ IT
                </Text>
              </Box>
              {/*
                Anonymous readers surface here when the article link was shared
                via a broadcast (SMS/WhatsApp) with no per-recipient attribution.
                Backend groups these as leadId='broadcast-anon' or missing and
                returns an aggregate summary so we can render a single row.
              */}
              {analytics.anonymousSummary && (
                <Box
                  style={{
                    borderBottom: '1px solid var(--aviary-disabled)',
                    background: 'var(--mantine-color-yellow-0)',
                  }}
                >
                  <Box p="md">
                    <Group justify="space-between" align="center" wrap="nowrap">
                      <Stack gap={2}>
                        <Text size="sm" fw={600} style={{ color: 'var(--aviary-ink)' }}>
                          {analytics.anonymousSummary.uniqueVisitors === 1
                            ? '1 anonymous reader'
                            : `${analytics.anonymousSummary.uniqueVisitors} anonymous readers`}
                        </Text>
                        <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
                          Broadcast link (SMS / WhatsApp) — no per-recipient attribution possible.
                        </Text>
                      </Stack>
                      <Stack gap={2} align="flex-end">
                        <Text size="sm" fw={500}>
                          {analytics.anonymousSummary.totalClicks}{' '}
                          {analytics.anonymousSummary.totalClicks === 1 ? 'click' : 'clicks'}
                        </Text>
                        {analytics.anonymousSummary.lastClickAt && (
                          <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
                            last {formatAgo(analytics.anonymousSummary.lastClickAt)}
                          </Text>
                        )}
                      </Stack>
                    </Group>
                  </Box>
                  {Array.isArray(analytics.anonymousSummary.visitors)
                    && analytics.anonymousSummary.visitors.length > 0 && (
                    <Table verticalSpacing="xs" style={{ background: 'transparent' }}>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th style={{ fontSize: 11, background: 'transparent' }}>Location</Table.Th>
                          <Table.Th style={{ fontSize: 11, background: 'transparent' }}>Browser</Table.Th>
                          <Table.Th style={{ fontSize: 11, background: 'transparent' }}>Device</Table.Th>
                          <Table.Th style={{ fontSize: 11, background: 'transparent', textAlign: 'right' }}>Clicks</Table.Th>
                          <Table.Th style={{ fontSize: 11, background: 'transparent', textAlign: 'right' }}>Last click</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {analytics.anonymousSummary.visitors.map((v) => {
                          const locationParts = [v.city, v.region, v.country].filter(Boolean);
                          const location = locationParts.length ? locationParts.join(', ') : '—';
                          const browser = [v.browser, v.os].filter(Boolean).join(' / ') || '—';
                          const device = v.deviceModel || '—';
                          return (
                            <Table.Tr key={v.ipHash}>
                              <Table.Td style={{ fontSize: 13 }}>{location}</Table.Td>
                              <Table.Td style={{ fontSize: 13 }}>{browser}</Table.Td>
                              <Table.Td style={{ fontSize: 13 }}>{device}</Table.Td>
                              <Table.Td style={{ fontSize: 13, textAlign: 'right' }}>{v.clickCount}</Table.Td>
                              <Table.Td style={{ fontSize: 13, textAlign: 'right' }}>
                                {v.lastClickAt ? formatAgo(v.lastClickAt) : '—'}
                              </Table.Td>
                            </Table.Tr>
                          );
                        })}
                      </Table.Tbody>
                    </Table>
                  )}
                </Box>
              )}
              {analytics.clicks.length === 0 && !analytics.anonymousSummary ? (
                <Box p="lg">
                  <Text size="sm" style={{ color: 'var(--aviary-muted)', textAlign: 'center' }}>
                    No readers yet. Once recipients open the article, they&apos;ll show here.
                  </Text>
                </Box>
              ) : analytics.clicks.length === 0 ? null : (
                <Table verticalSpacing="sm" striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Person</Table.Th>
                      <Table.Th>Location</Table.Th>
                      <Table.Th>Device</Table.Th>
                      <Table.Th style={{ textAlign: 'right' }}>Dwell</Table.Th>
                      <Table.Th style={{ textAlign: 'right' }}>% Read</Table.Th>
                      <Table.Th style={{ textAlign: 'right' }}>Opens</Table.Th>
                      <Table.Th>Outcome</Table.Th>
                      <Table.Th style={{ textAlign: 'right' }} />
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {analytics.clicks.map((row) => {
                      const forwardedReaders = Array.isArray(row.forwardedReaders) ? row.forwardedReaders : [];
                      const own = row.own || {};
                      const ownVisits = Array.isArray(own.visits) ? own.visits : [];
                      const hasForwards = forwardedReaders.length > 0;
                      const hasOwnMulti = ownVisits.length > 1;
                      const isExpandable = hasForwards || hasOwnMulti;
                      const isExpanded = Boolean(expandedByLeadId[row.leadId]);
                      const showAll = Boolean(showAllByLeadId[row.leadId]);
                      const visibleForwarded = showAll ? forwardedReaders : forwardedReaders.slice(0, 5);
                      const hiddenCount = Math.max(0, forwardedReaders.length - 5);
                      return (
                        <Fragment key={row.leadId}>
                          <Table.Tr>
                            <Table.Td>
                              <Group gap="xs" wrap="nowrap" align="center">
                                {isExpandable ? (
                                  <UnstyledButton
                                    onClick={() => {
                                      setExpandedByLeadId((prev) => ({ ...prev, [row.leadId]: !prev[row.leadId] }));
                                    }}
                                    aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                                  >
                                    {isExpanded ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                                  </UnstyledButton>
                                ) : (
                                  <Box style={{ width: 14 }} />
                                )}
                                <Text size="sm" fw={600} style={{ color: 'var(--aviary-ink)' }}>{row.name}</Text>
                              </Group>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>{formatLocation(own)}</Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>{formatDevice(own)}</Text>
                            </Table.Td>
                            <Table.Td style={{ textAlign: 'right' }}>
                              <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>{formatDwell(own.maxDwellSeconds)}</Text>
                            </Table.Td>
                            <Table.Td style={{ textAlign: 'right' }}>
                              <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>{formatReadPct(own.maxScrollPct)}</Text>
                            </Table.Td>
                            <Table.Td style={{ textAlign: 'right' }}>
                              <Text size="sm" fw={600} style={{ color: 'var(--aviary-ink)' }}>
                                {Number.isFinite(Number(own.opens)) ? Number(own.opens) : row.openCount}
                              </Text>
                            </Table.Td>
                            <Table.Td>{outcomeBadge(row.outcome)}</Table.Td>
                            <Table.Td style={{ textAlign: 'right' }}>
                              <UnstyledButton
                                onClick={() => navigate(`/v2/leads/${row.leadId}`, {
                                  state: { back: { path: `/v2/articles/${slug}`, label: article.title } },
                                })}
                              >
                                <Text size="xs" c="sage.7" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                  Open <IconExternalLink size={12} />
                                </Text>
                              </UnstyledButton>
                            </Table.Td>
                          </Table.Tr>
                          {isExpanded && hasOwnMulti && ownVisits.map((v, i) => (
                            <Table.Tr key={`${row.leadId}:own:${i}`}>
                              <Table.Td>
                                <Text size="xs" style={{ color: 'var(--aviary-muted)', paddingLeft: 22 }}>
                                  · {formatVisitTs(v.ts)}
                                </Text>
                              </Table.Td>
                              <Table.Td>
                                <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>{formatLocation(v)}</Text>
                              </Table.Td>
                              <Table.Td>
                                <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>{formatDevice(v)}</Text>
                              </Table.Td>
                              <Table.Td style={{ textAlign: 'right' }}>
                                <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>{formatDwell(v.dwellSeconds)}</Text>
                              </Table.Td>
                              <Table.Td style={{ textAlign: 'right' }}>
                                <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>{formatReadPct(v.scrollPct)}</Text>
                              </Table.Td>
                              <Table.Td colSpan={3} />
                            </Table.Tr>
                          ))}
                          {isExpanded && hasForwards && (
                            <>
                              {visibleForwarded.map((reader) => {
                                const readerVisits = Array.isArray(reader.visits) ? reader.visits : [];
                                const readerExpandable = readerVisits.length > 1;
                                const readerKey = `${row.leadId}:${reader.label}`;
                                const readerExpanded = Boolean(expandedReaderKey[readerKey]);
                                return (
                                  <Fragment key={readerKey}>
                                    <Table.Tr>
                                      <Table.Td>
                                        <Group gap="xs" wrap="nowrap" align="center" style={{ paddingLeft: 22 }}>
                                          {readerExpandable ? (
                                            <UnstyledButton
                                              onClick={() => {
                                                setExpandedReaderKey((prev) => ({ ...prev, [readerKey]: !prev[readerKey] }));
                                              }}
                                              aria-label={readerExpanded ? 'Collapse visits' : 'Expand visits'}
                                            >
                                              {readerExpanded ? <IconChevronDown size={12} /> : <IconChevronRight size={12} />}
                                            </UnstyledButton>
                                          ) : (
                                            <Box style={{ width: 12 }} />
                                          )}
                                          <Text size="sm" style={{ color: 'var(--aviary-ink)' }}>
                                            {reader.label}{reader.isReturn ? ' (returning)' : ''}
                                          </Text>
                                        </Group>
                                      </Table.Td>
                                      <Table.Td>
                                        <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>{formatLocation(reader)}</Text>
                                      </Table.Td>
                                      <Table.Td>
                                        <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>{formatDevice(reader)}</Text>
                                      </Table.Td>
                                      <Table.Td style={{ textAlign: 'right' }}>
                                        <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>{formatDwell(reader.maxDwellSeconds)}</Text>
                                      </Table.Td>
                                      <Table.Td style={{ textAlign: 'right' }}>
                                        <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>{formatReadPct(reader.maxScrollPct)}</Text>
                                      </Table.Td>
                                      <Table.Td style={{ textAlign: 'right' }}>
                                        <Text size="sm" fw={600} style={{ color: 'var(--aviary-ink)' }}>{reader.opens}</Text>
                                      </Table.Td>
                                      <Table.Td colSpan={2} />
                                    </Table.Tr>
                                    {readerExpanded && readerVisits.map((v, i) => (
                                      <Table.Tr key={`${readerKey}:visit:${i}`}>
                                        <Table.Td>
                                          <Text size="xs" style={{ color: 'var(--aviary-muted)', paddingLeft: 44 }}>
                                            · {formatVisitTs(v.ts)}
                                          </Text>
                                        </Table.Td>
                                        <Table.Td>
                                          <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>{formatLocation(v)}</Text>
                                        </Table.Td>
                                        <Table.Td>
                                          <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>{formatDevice(v)}</Text>
                                        </Table.Td>
                                        <Table.Td style={{ textAlign: 'right' }}>
                                          <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>{formatDwell(v.dwellSeconds)}</Text>
                                        </Table.Td>
                                        <Table.Td style={{ textAlign: 'right' }}>
                                          <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>{formatReadPct(v.scrollPct)}</Text>
                                        </Table.Td>
                                        <Table.Td colSpan={3} />
                                      </Table.Tr>
                                    ))}
                                  </Fragment>
                                );
                              })}
                              {!showAll && hiddenCount > 0 && (
                                <Table.Tr>
                                  <Table.Td colSpan={8}>
                                    <UnstyledButton
                                      onClick={() => setShowAllByLeadId((prev) => ({ ...prev, [row.leadId]: true }))}
                                      style={{ paddingLeft: 22 }}
                                    >
                                      <Text size="xs" c="sage.7">
                                        show {hiddenCount} more {hiddenCount === 1 ? 'reader' : 'readers'} on this link
                                      </Text>
                                    </UnstyledButton>
                                  </Table.Td>
                                </Table.Tr>
                              )}
                            </>
                          )}
                        </Fragment>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              )}
              {analytics.clicks.length > 0 && (
                <Box p="md" pt="xs" style={{ borderTop: '1px solid var(--aviary-disabled)' }}>
                  <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
                    Readers are device+network fingerprints. One person on two devices counts twice; one office network can merge two people. Counts are indicative.
                  </Text>
                </Box>
              )}
            </Paper>
          </Stack>
        )}

        {!isExternalArticle && (
          <Paper radius="md" withBorder p={0} style={{ overflow: 'hidden', background: '#fff' }}>
          <Box
            p="lg"
            style={{ borderBottom: '1px solid var(--aviary-disabled)' }}
          >
            <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.3 }}>
              PREVIEW
            </Text>
          </Box>
          {isRawArticle ? (
            <Box px="lg" py="md">
              <iframe
                src={publicHref}
                title="Article preview"
                sandbox="allow-scripts"
                style={{
                  width: '100%',
                  height: 600,
                  border: '1px solid #e0e0e0',
                  borderRadius: 6,
                }}
              />
            </Box>
          ) : (
            <Box
              px="lg"
              py="md"
              style={{
                maxHeight: 720,
                overflow: 'auto',
                lineHeight: 1.6,
                color: 'var(--aviary-ink)',
              }}
              // bodyHtml is already sanitised server-side on upload/create.
              dangerouslySetInnerHTML={{ __html: article.bodyHtml || '<em>No content.</em>' }}
            />
          )}
          </Paper>
        )}
      </Stack>
    </Box>
  );
}
